import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ClipboardList, ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle,
  User, FileSignature, Sparkles, Download,
} from 'lucide-react';
import Header from '../components/Header';
import { SECTIONS } from '../lib/sectionTheme';
import { parseMedicare, formatDigitsDisplay, formatMedicare } from '../lib/medicare';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const P = SECTIONS.preparation;

// ────────────────────────────────────────────────────────────────────────────
// Index: list of available screening forms
// ────────────────────────────────────────────────────────────────────────────
export function ScreeningIndexPage() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/screening-forms`, { withCredentials: true })
      .then(r => setForms(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="screening-index">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4" style={{ color: P.color }} />
              <span className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: P.color }}>Patient screening</span>
            </div>
            <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
              Start a <span style={{ color: P.color }}>screening</span>
            </h1>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
              Hand the tablet to the patient. Their answers are time-stamped, flagged for radiologist review where needed, and stored to the patient record.
            </p>
          </div>
          <a
            href={`${API}/screening-forms/download-all.pdf`}
            target="_blank"
            rel="noreferrer"
            data-testid="screening-download-all"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl border shrink-0"
            style={{ borderColor: `${P.color}40`, color: P.color }}
          >
            <Download className="w-4 h-4" /> Download all (PDF)
          </a>
        </div>

        {loading ? (
          <p className="p-6 text-center text-sm text-muted-foreground">Loading forms…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="screening-forms-list">
            {forms.map(f => (
              <Link
                key={f.form_id}
                to={`/screening/new/${f.form_id}`}
                data-testid={`screening-form-card-${f.form_id}`}
                className="group p-5 bg-card border border-border rounded-2xl hover:border-[--accent] hover:shadow-sm transition-all"
                style={{ '--accent': P.color }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: P.color }}>{f.modality}</p>
                    <h3 className="font-['Outfit'] text-lg font-semibold text-foreground mt-1">{f.title}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {f.question_count} question{f.question_count === 1 ? '' : 's'} · v{f.version}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform shrink-0" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Fill-in flow: patient-facing, tablet-optimized
// ────────────────────────────────────────────────────────────────────────────
export function ScreeningFormPage() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('patient'); // patient | questions | consent | submitted
  const [patient, setPatient] = useState({ name: '', dob: '', medicare: '', phone: '', scheduled_exam: '' });
  const [answers, setAnswers] = useState({});
  const [consent, setConsent] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    axios.get(`${API}/screening-forms/${formId}`, { withCredentials: true })
      .then(r => setForm(r.data))
      .catch(() => setErr('Form not found'))
      .finally(() => setLoading(false));
  }, [formId]);

  if (loading) return <Loading label="Loading form…" />;
  if (!form) return <div className="min-h-screen bg-background"><Header /><div className="p-10 text-center text-muted-foreground">{err || 'Form not found'}</div></div>;

  const required = (form.questions || []).filter(q => q.required);
  const missing = required.filter(q => !(q.id in answers) || answers[q.id] === '');
  const canSubmit = missing.length === 0 && (!form.requires_consent || (consent && typedName.trim()));

  const submit = async () => {
    setSubmitting(true); setErr('');
    try {
      const r = await axios.post(`${API}/screenings`, {
        form_id: formId, patient, answers, patient_typed_name: typedName.trim(), consent_given: consent,
      }, { withCredentials: true });
      setResult(r.data);
      setStep('submitted');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Submission failed');
    }
    setSubmitting(false);
  };

  if (step === 'submitted') {
    const flagged = (result?.flags || []).length > 0;
    return (
      <div className="min-h-screen bg-background" data-testid="screening-submitted">
        <Header />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-6"
            style={{ backgroundColor: flagged ? '#F59E0B15' : '#16A34A15' }}>
            {flagged
              ? <AlertTriangle className="w-10 h-10" style={{ color: '#F59E0B' }} />
              : <CheckCircle2 className="w-10 h-10" style={{ color: '#16A34A' }} />}
          </div>
          <h1 className="font-['Outfit'] text-3xl font-bold text-foreground">Thank you, {patient.name.split(' ')[0] || 'submitted'}.</h1>
          <p className="mt-2 text-sm text-muted-foreground">Your answers have been recorded.</p>
          {flagged && (
            <div className="mt-6 p-4 border border-amber-500/30 bg-amber-500/5 rounded-xl text-left" data-testid="screening-flags">
              <p className="text-sm font-semibold text-amber-600 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" /> {result.flags.length} item{result.flags.length === 1 ? '' : 's'} flagged for radiologist review
              </p>
              <ul className="text-xs text-foreground space-y-1">
                {result.flags.map((f, i) => <li key={i}>• {f}</li>)}
              </ul>
            </div>
          )}
          <div className="mt-8 flex items-center justify-center gap-2">
            <Link to="/screening" data-testid="screening-done-another"
              className="text-sm font-semibold px-4 py-2 rounded-lg border border-border hover:bg-accent">Another screening</Link>
            <Link to="/" data-testid="screening-done-home"
              className="text-sm font-semibold px-4 py-2 rounded-lg text-white"
              style={{ backgroundColor: P.color }}>Back to home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="screening-form-page">
      <Header />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button onClick={() => navigate('/screening')} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-5"
          data-testid="screening-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: P.color }}>{form.modality} · v{form.version}</p>
        <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-bold tracking-tight text-foreground mt-1">{form.title}</h1>
        {form.intro && <p className="mt-3 text-sm text-muted-foreground">{form.intro}</p>}

        {/* Step indicator */}
        <div className="flex items-center gap-2 my-6" data-testid="screening-step-indicator">
          {['patient', 'questions', ...(form.requires_consent ? ['consent'] : [])].map((s, i, arr) => {
            const isActive = step === s;
            const isDone = arr.indexOf(step) > i;
            return (
              <div key={s} className="flex items-center gap-2 flex-1">
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold"
                  style={{
                    backgroundColor: isActive ? P.color : isDone ? `${P.color}30` : 'hsl(var(--muted))',
                    color: isActive ? 'white' : isDone ? P.color : 'hsl(var(--muted-foreground))',
                  }}>
                  {isDone ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                </div>
                <span className="text-xs font-semibold capitalize" style={{ color: isActive ? P.color : 'hsl(var(--muted-foreground))' }}>{s}</span>
                {i < arr.length - 1 && <div className="flex-1 h-px bg-border" />}
              </div>
            );
          })}
        </div>

        {/* Step: patient details */}
        {step === 'patient' && (
          <div className="bg-card border border-border rounded-2xl p-6 space-y-4" data-testid="screening-step-patient">
            <TabletField label="Full name" required value={patient.name}
              onChange={v => setPatient({ ...patient, name: v })} testid="patient-name" />
            <TabletField label="Date of birth" value={patient.dob}
              onChange={v => setPatient({ ...patient, dob: v })} placeholder="e.g. 14/03/1972" testid="patient-dob" />
            <TabletMedicareField value={patient.medicare}
              onChange={v => setPatient({ ...patient, medicare: v })} testid="patient-medicare" />
            <TabletField label="Phone" value={patient.phone}
              onChange={v => setPatient({ ...patient, phone: v })} placeholder="e.g. 0412 345 678" testid="patient-phone" />
            <TabletField label="Today's exam / procedure" value={patient.scheduled_exam}
              onChange={v => setPatient({ ...patient, scheduled_exam: v })} placeholder="e.g. CT Abdomen with contrast" testid="patient-exam" />

            <div className="flex justify-end pt-2 border-t border-border">
              <button onClick={() => setStep('questions')}
                disabled={!patient.name.trim()}
                data-testid="screening-next-questions"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-5 py-2.5 rounded-lg text-white disabled:opacity-40"
                style={{ backgroundColor: P.color }}>
                Start questions <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step: questions */}
        {step === 'questions' && (
          <div className="bg-card border border-border rounded-2xl p-6 space-y-4" data-testid="screening-step-questions">
            {(form.questions || []).map((q, i) => (
              <QuestionField key={q.id} q={q} idx={i}
                value={answers[q.id]}
                onChange={v => setAnswers({ ...answers, [q.id]: v })} />
            ))}

            {missing.length > 0 && (
              <p className="text-xs text-red-500" data-testid="screening-missing-required">
                {missing.length} required question{missing.length === 1 ? '' : 's'} still to answer.
              </p>
            )}

            <div className="flex justify-between pt-2 border-t border-border">
              <button onClick={() => setStep('patient')}
                className="text-sm font-semibold px-4 py-2.5 rounded-lg border border-border">Back</button>
              <button onClick={() => setStep(form.requires_consent ? 'consent' : 'questions')}
                onClickCapture={() => { if (!form.requires_consent && missing.length === 0) submit(); }}
                disabled={missing.length > 0 || submitting}
                data-testid="screening-next-from-questions"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-5 py-2.5 rounded-lg text-white disabled:opacity-40"
                style={{ backgroundColor: P.color }}>
                {form.requires_consent ? <>Next <ArrowRight className="w-4 h-4" /></> : <>Submit <CheckCircle2 className="w-4 h-4" /></>}
              </button>
            </div>
          </div>
        )}

        {/* Step: consent */}
        {step === 'consent' && form.requires_consent && (
          <div className="bg-card border border-border rounded-2xl p-6 space-y-4" data-testid="screening-step-consent">
            <div className="p-4 border border-border rounded-xl bg-muted/30">
              <FileSignature className="w-5 h-5 mb-2" style={{ color: P.color }} />
              <p className="text-sm text-foreground leading-relaxed">{form.consent_text}</p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer" data-testid="screening-consent-check">
              <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)}
                className="w-5 h-5 mt-0.5 rounded border-border accent-[--p]" style={{ '--p': P.color }} />
              <span className="text-sm text-foreground">I have read and agree to the statement above.</span>
            </label>

            <TabletField label="Type your full name as signature" required value={typedName}
              onChange={setTypedName} testid="screening-typed-name" />
            <p className="text-[11px] text-muted-foreground">
              Typing your full name above is your electronic signature and is captured with today's date &amp; time.
            </p>

            {err && <p className="text-xs text-red-500" data-testid="screening-error">{err}</p>}

            <div className="flex justify-between pt-2 border-t border-border">
              <button onClick={() => setStep('questions')}
                className="text-sm font-semibold px-4 py-2.5 rounded-lg border border-border">Back</button>
              <button onClick={submit} disabled={!canSubmit || submitting}
                data-testid="screening-submit"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-5 py-2.5 rounded-lg text-white disabled:opacity-40"
                style={{ backgroundColor: P.color }}>
                <CheckCircle2 className="w-4 h-4" /> {submitting ? 'Submitting…' : 'Submit screening'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Loading({ label }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-4 border-[#3fa758] border-t-transparent rounded-full" />
      <span className="ml-3 text-sm text-muted-foreground">{label}</span>
    </div>
  );
}

function TabletField({ label, value, onChange, required, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-foreground">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      <input
        type="text"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full mt-1 text-base bg-background border border-border rounded-xl px-4 py-3"
      />
    </label>
  );
}

function TabletMedicareField({ value, onChange, testid }) {
  const parsed = parseMedicare(value);
  const [digits, setDigits] = useState(parsed.digits);
  const [irn, setIrn] = useState(parsed.irn);

  useEffect(() => {
    const p = parseMedicare(value);
    if (p.digits !== digits) setDigits(p.digits);
    if (p.irn !== irn) setIrn(p.irn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (d, i) => {
    const formatted = d.length === 0 && i.length === 0 ? '' : formatMedicare(d, i);
    onChange(formatted);
  };
  const onDigitsChange = (raw) => {
    const d = raw.replace(/\D/g, '').slice(0, 10);
    setDigits(d);
    emit(d, irn);
  };
  const onIrnChange = (raw) => {
    const i = raw.replace(/[^1-9]/g, '').slice(0, 1);
    setIrn(i);
    emit(digits, i);
  };

  const partial = digits.length > 0 || irn.length > 0;
  const complete = digits.length === 10 && irn.length === 1;
  const invalid = partial && !complete;
  const borderColor = invalid ? '#DC2626' : 'hsl(var(--border))';

  return (
    <div>
      <span className="text-xs font-semibold text-foreground">Medicare number (optional)</span>
      <div className="mt-1 grid grid-cols-[1fr_88px] gap-2">
        <input type="text" inputMode="numeric" autoComplete="off"
          value={formatDigitsDisplay(digits)}
          onChange={e => onDigitsChange(e.target.value)}
          placeholder="XXXX XXXXX X"
          maxLength={12}
          aria-label="Medicare card number"
          data-testid={testid}
          className="w-full text-base bg-background border rounded-xl px-4 py-3 font-mono tracking-wider"
          style={{ borderColor }} />
        <input type="text" inputMode="numeric" autoComplete="off"
          value={irn}
          onChange={e => onIrnChange(e.target.value)}
          placeholder="IRN"
          maxLength={1}
          aria-label="Medicare IRN"
          data-testid={testid ? `${testid}-irn` : undefined}
          className="w-full text-base bg-background border rounded-xl px-4 py-3 font-mono text-center"
          style={{ borderColor }} />
      </div>
      <p className="text-[11px] text-muted-foreground mt-1">
        10-digit card number + 1-digit IRN (family-line number, 1–9).
        {invalid && <span className="ml-1 font-semibold" style={{ color: '#DC2626' }}>Enter all 10 digits and the IRN.</span>}
      </p>
    </div>
  );
}

function QuestionField({ q, idx, value, onChange }) {
  if (q.type === 'yes_no') {
    return (
      <div data-testid={`screening-q-${q.id}`} className="border-b border-border/40 last:border-0 pb-4 last:pb-0">
        <p className="text-sm font-semibold text-foreground">
          <span className="text-muted-foreground font-normal mr-1">{idx + 1}.</span>
          {q.label}
          {q.required && <span className="text-red-500 ml-0.5">*</span>}
        </p>
        {q.help && <p className="text-xs text-muted-foreground mt-1">{q.help}</p>}
        <div className="flex gap-2 mt-3">
          {['Yes', 'No'].map(opt => {
            const active = value === opt;
            return (
              <button key={opt} type="button"
                onClick={() => onChange(opt)}
                data-testid={`screening-q-${q.id}-${opt.toLowerCase()}`}
                className="flex-1 text-sm font-semibold py-3 rounded-xl border transition-colors"
                style={active
                  ? { backgroundColor: P.color, borderColor: P.color, color: 'white' }
                  : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--foreground))' }}>
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (q.type === 'select') {
    return (
      <div data-testid={`screening-q-${q.id}`} className="border-b border-border/40 last:border-0 pb-4 last:pb-0">
        <p className="text-sm font-semibold text-foreground">
          <span className="text-muted-foreground font-normal mr-1">{idx + 1}.</span>
          {q.label}
          {q.required && <span className="text-red-500 ml-0.5">*</span>}
        </p>
        <div className="space-y-2 mt-3">
          {(q.options || []).map(opt => {
            const active = value === opt;
            return (
              <button key={opt} type="button"
                onClick={() => onChange(opt)}
                data-testid={`screening-q-${q.id}-${opt.slice(0, 10).replace(/\s+/g, '-')}`}
                className="w-full text-left text-sm py-3 px-4 rounded-xl border transition-colors"
                style={active
                  ? { backgroundColor: `${P.color}15`, borderColor: P.color, color: P.color, fontWeight: 600 }
                  : { borderColor: 'hsl(var(--border))' }}>
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  return (
    <div data-testid={`screening-q-${q.id}`} className="border-b border-border/40 last:border-0 pb-4 last:pb-0">
      <p className="text-sm font-semibold text-foreground">
        <span className="text-muted-foreground font-normal mr-1">{idx + 1}.</span>
        {q.label}
        {q.required && <span className="text-red-500 ml-0.5">*</span>}
      </p>
      {q.help && <p className="text-xs text-muted-foreground mt-1">{q.help}</p>}
      <input type="text"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={q.placeholder}
        data-testid={`screening-q-${q.id}-input`}
        className="w-full mt-2 text-base bg-background border border-border rounded-xl px-4 py-3" />
    </div>
  );
}
